import { FC, useCallback } from 'react';
import {
  ObjectType,
  Spinner,
  useObjectLockQuery,
  useDataObjectGroupQuery,
  checkLockOnPage,
  PermissionCode,
  useHasPermission,
} from '@atlas-frontend/sdk';
import { path } from '../../paths';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { useNumericParam } from '../../common/useEntityIdParam';
import { Forbidden } from '../Forbidden';
import { NotFound } from '../NotFound';
import { DataObjectGroupForm } from './DataObjectGroupForm';

export const DataObjectGroupEdit = () => {
  const dataObjectGroupId = useNumericParam('id');
  const hasPermission = useHasPermission(
    PermissionCode.DATA_OBJECT_GROUP_MANAGE,
  );

  if (!dataObjectGroupId) {
    return <NotFound />;
  }

  return (
    <Forbidden isAllowed={hasPermission}>
      <DataObjectGroupEditInner dataObjectGroupId={dataObjectGroupId} />
    </Forbidden>
  );
};

const DataObjectGroupEditInner: FC<{ dataObjectGroupId: number }> = ({
  dataObjectGroupId,
}) => {
  const {
    data: dataObjectGroup,
    isLoading,
    refetch,
  } = useDataObjectGroupQuery({
    id: dataObjectGroupId,
  });
  const { data: lockData } = useObjectLockQuery({
    objectId: dataObjectGroupId,
    objectType: ObjectType.DataObjectGroup,
  });
  const navigateToDataObjectGroupView = useNavigateTo(
    path.dataObjectGroupDetails,
    {
      id: dataObjectGroupId,
    },
  );

  checkLockOnPage(lockData, () => navigateToDataObjectGroupView());

  const handleCancel = useCallback(() => {
    navigateToDataObjectGroupView();
  }, [navigateToDataObjectGroupView]);
  const handleSuccess = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!dataObjectGroup) {
    return null;
  }

  return (
    <DataObjectGroupForm
      mode="edit"
      id={dataObjectGroupId}
      defaultValues={dataObjectGroup}
      onClickCancel={handleCancel}
      onSuccess={handleSuccess}
    />
  );
};
